import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSupportChat } from "../context/SupportChatContext";
import { useTheme } from "../context/ThemeContext";
import BottomNav from "../components/BottomNav";

function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { unreadCount } = useSupportChat();
  const { colors, isDarkMode } = useTheme(); 
  
  // State for Welcome Card - Defaults to false to prevent flickering
  const [showWelcome, setShowWelcome] = useState(false);

  const assignedProductsCount = user?.assignedProducts?.length || 0;
  const isRestricted = user?.restricted || user?.blocked; 
  const isProfilePage = location.pathname === "/profile";

  // Immediate Logic for Welcome Card
  useEffect(() => {
    const hasSeenWelcome = sessionStorage.getItem("welcomeShown");
    
    if (!hasSeenWelcome) {
      setShowWelcome(true);
    }
  }, []);

  const closeWelcome = () => {
    sessionStorage.setItem("welcomeShown", "true");
    setShowWelcome(false);
  };

  // System logic: Fix for mobile VH units
  useEffect(() => {
    const fixHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    window.addEventListener('resize', fixHeight);
    fixHeight();
    return () => window.removeEventListener('resize', fixHeight);
  }, []);

  // Extract tenant context identifier
  const tenantId = user?.adminId || localStorage.getItem("adminId") || "global";

  return (
    <div 
      style={{
        ...styles.appContainer, 
        backgroundColor: colors.bg, 
        transition: "background-color 0.3s ease" 
      }}
      data-tenant-scope={tenantId}
    >
      
      {/* GLASS WELCOME MODAL */}
      {showWelcome && (
        <div style={styles.welcomeOverlay}>
          <div style={{
            ...styles.welcomeCard,
            backgroundColor: isDarkMode ? "rgba(30, 30, 30, 0.8)" : "rgba(255, 255, 255, 0.85)",
            border: `1px solid ${colors.brand}44`,
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
          }}>
            <div style={{ fontSize: "50px", marginBottom: "10px" }}>🚢</div>
            <h2 style={{ 
                color: colors.brand, 
                margin: "0 0 15px 0", 
                fontSize: "22px", 
                fontWeight: "900",
                letterSpacing: "-0.5px" 
            }}>
              MSS System Authentication Successful
            </h2>
            <p style={{ 
                color: colors.text, 
                fontSize: "15px", 
                lineHeight: "1.6", 
                opacity: 0.9, 
                marginBottom: "30px",
                padding: "0 10px" 
            }}>
              Welcome aboard, Operator. Your secure uplink to the Maritime Supply terminal is now active. If you encounter technical discrepancies, initiate a support request immediately. Review the mandatory <b>User Guide</b> in the top navigation bar to ensure compliance with current shipping and settlement protocols.
            </p>
            <div style={{ fontSize: "11px", opacity: 0.6, marginBottom: "20px" }}>
              © 2026 MARITIME SUPPLY SUITES
            </div>
            <button 
              onClick={closeWelcome}
              style={{
                ...styles.okBtn,
                backgroundColor: colors.brand,
                color: isDarkMode ? "#000" : "#fff",
              }}
            >
              GOT IT
            </button>
          </div>
        </div>
      )}

      {/* GLOBAL SYSTEM ALERT */}
      {isRestricted && (
        <div style={{...styles.statusBanner, background: colors.danger || "#ef4444"}}>
          ⚠️ ACCOUNT RESTRICTED: COMPLETE VERIFICATION VIA CUSTOMER SUPPORT AGENT
        </div>
      )}

      <div style={styles.contentWrapper}>
        {/* INTERACTION LOCK */}
        {isRestricted && !isProfilePage && (
          <div style={styles.auditOverlay}>
            <div style={{
              ...styles.auditModal, 
              backgroundColor: colors.card, 
              borderColor: colors.danger || "#ef4444"
            }}>
              <h3 style={{ margin: "0 0 10px 0", color: colors.text }}>Access Suspended</h3>
              <p style={{ margin: 0, fontSize: "14px", color: colors.muted }}>
                Verification required by Maritime Compliance.
              </p>
              <button 
                onClick={() => navigate("/profile")} 
                style={{...styles.liaisonBtn, backgroundColor: colors.brand}}
              >
                CONTACT CUSTOMER SUPPORT
              </button>
            </div>
          </div>
        )}
        
        <div style={{ width: "100%", flex: 1 }}>
          <Outlet />
        </div>
      </div>

      <BottomNav 
        unreadMessagesCount={unreadCount} 
        assignedProductsCount={assignedProductsCount} 
      />
    </div>
  );
}

const styles = {
  appContainer: {
    display: "flex",
    flexDirection: "column",
    height: "calc(var(--vh, 1vh) * 100)",
    width: "100%",
    overflow: "hidden", 
    position: "fixed",
    top: 0,
    left: 0,
    fontFamily: "'Inter', sans-serif"
  },
  contentWrapper: {
    flex: 1,
    width: "100%",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    paddingBottom: "90px", 
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column"
  },
  welcomeOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    zIndex: 99999,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px"
  },
  welcomeCard: {
    width: "100%",
    maxWidth: "380px",
    padding: "40px 25px",
    borderRadius: "30px",
    textAlign: "center",
    position: "relative",
  },
  okBtn: {
    width: "100%",
    padding: "18px",
    borderRadius: "15px",
    border: "none",
    fontWeight: "900",
    fontSize: "14px",
    letterSpacing: "1px",
    cursor: "pointer",
    transition: "transform 0.2s ease"
  },
  statusBanner: {
    color: "#ffffff",
    padding: "10px",
    textAlign: "center",
    fontSize: "11px",
    letterSpacing: "1px",
    fontWeight: "800",
    zIndex: 10005,
    width: "100%",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)"
  },
  auditOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.85)",
    zIndex: 10000,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backdropFilter: "blur(4px)"
  },
  auditModal: {
    padding: "30px",
    borderRadius: "12px",
    textAlign: "center",
    border: `1px solid`,
    boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
    maxWidth: "85%"
  },
  liaisonBtn: {
    color: "#000",
    border: "none",
    padding: "14px 28px",
    borderRadius: "8px",
    fontWeight: "900",
    fontSize: "12px",
    marginTop: "20px",
    cursor: "pointer",
    letterSpacing: "1px"
  }
};

export default MainLayout;